// src/components/SimpleVWAPChart.tsx

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  ResponsiveContainer, ComposedChart, Area, Line,
  CartesianGrid, XAxis, YAxis, Tooltip,
} from 'recharts';
import { useMarketData } from '../context/MarketContext';
import { useVwapData } from '../store/hooks';
import { useTimeSeries, TimeRange } from '../hooks/useTimeSeries';
import { fetchVwapRange, OhlcvPoint } from '../api/series';
import TimeRangePicker from './TimeRangePicker';

export interface SimpleVWAPChartProps {
  symbol?: string;
  height?: number;
  microprice?: number;
}

const ALPHA = 0.15; // EWM smoothing factor ≈ span-12

// Map a historical API point to a TimeSeriesPoint
const mapApiPoint = (p: OhlcvPoint) => ({
  timeValue: new Date(p.ts).getTime(),
  ts: p.ts,
  vwap: p.vwap ?? undefined,
});

// Map a live Redux VWAP point to a TimeSeriesPoint
const mapLivePoint = (p: any) => ({
  timeValue: new Date(p.timestamp).getTime(),
  ts: p.timestamp,
  vwap: p.vwap,
});

export default function SimpleVWAPChart({
  symbol = 'LPPJ',
  height = 300,
  microprice,
}: SimpleVWAPChartProps) {
  const { selectedSymbol, labelForSecId } = useMarketData();
  const actualSymbol = (symbol || selectedSymbol).toUpperCase();
  const liveVwapData = useVwapData(actualSymbol);

  const [range, setRange] = useState<TimeRange>('live');

  const fetcher = useCallback(
    (sym: string, lookback: string, token: string | null) =>
      fetchVwapRange(sym, lookback, token),
    [],
  );

  const { data, loading, error, isLive } = useTimeSeries<OhlcvPoint>({
    symbol: actualSymbol,
    range,
    fetchHistorical: fetcher,
    mapApiPoint,
    liveData: liveVwapData,
    mapLivePoint,
  });

  // EWM microprice overlay
  const ewmpRef = useRef<number | null>(null);
  const [ewmpHistory, setEwmpHistory] = useState<{ timeValue: number; ewmp: number }[]>([]);

  useEffect(() => {
    ewmpRef.current = null;
    setEwmpHistory([]);
  }, [actualSymbol]);

  useEffect(() => {
    if (microprice == null || microprice <= 0) return;
    ewmpRef.current = ewmpRef.current == null
      ? microprice
      : ALPHA * microprice + (1 - ALPHA) * ewmpRef.current;
    const ewmp = ewmpRef.current;
    setEwmpHistory(prev => [...prev.slice(-499), { timeValue: Date.now(), ewmp }]);
  }, [microprice]);

  // Merge VWAP data with EWM microprice — only show EWM in live mode (intraday)
  const combinedData = useMemo(() => {
    const vwapPts = data.map(d => ({
      timeValue: d.timeValue,
      vwap: d.vwap as number | undefined,
      ewmp: undefined as number | undefined,
    }));
    if (!isLive || ewmpHistory.length === 0) return vwapPts;
    const ewmpPts = ewmpHistory.map(e => ({
      timeValue: e.timeValue,
      vwap: undefined as number | undefined,
      ewmp: e.ewmp,
    }));
    return [...vwapPts, ...ewmpPts].sort((a, b) => a.timeValue - b.timeValue);
  }, [data, ewmpHistory, isLive]);

  const xAxisDomain = useMemo(() => {
    if (combinedData.length === 0) return undefined;
    const times = combinedData.map(d => d.timeValue);
    const min = Math.min(...times);
    const max = Math.max(...times);
    const pad = (max - min) * 0.02 || 60_000;
    return [min - pad, max + pad];
  }, [combinedData]);

  const formatTick = (v: number) => {
    const d = new Date(v);
    // For ranges > 1 day show date, otherwise show time
    if (range === '1m' || range === '3m') {
      return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
    }
    if (range === '1w') {
      return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
    }
    return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  const lastPoint = data.length > 0 ? data[data.length - 1] : null;

  const inner = () => {
    if (loading) {
      return (
        <div style={centreBox(height)}>
          <div style={{ color: '#7e8a96' }}>Loading…</div>
        </div>
      );
    }
    if (error) {
      return (
        <div style={centreBox(height)}>
          <div style={{ color: '#ff5c5c', fontSize: 13 }}>Error: {error}</div>
        </div>
      );
    }
    if (combinedData.length === 0) {
      return (
        <div style={centreBox(height)}>
          <div style={{ color: '#7e8a96', fontSize: 13 }}>
            {isLive ? `Waiting for live VWAP data…` : `No data for ${range.toUpperCase()} range`}
          </div>
        </div>
      );
    }
    return (
      <div style={{ height, width: '100%', boxSizing: 'border-box' }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={combinedData}>
            <defs>
              <linearGradient id="vwapGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%"   stopColor="#6366f1" stopOpacity={0.8} />
                <stop offset="50%"  stopColor="#c77dff" stopOpacity={0.6} />
                <stop offset="100%" stopColor="#c77dff" stopOpacity={0}   />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#2a3540" />
            <XAxis
              type="number"
              dataKey="timeValue"
              domain={xAxisDomain}
              stroke="#7e8a96"
              fontSize={11}
              tickFormatter={formatTick}
              allowDecimals={false}
            />
            <YAxis
              stroke="#7e8a96"
              fontSize={11}
              domain={['dataMin - 0.5', 'dataMax + 0.5']}
              tickFormatter={(v: number) => v.toFixed(0)}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: '#10151b',
                border: '1px solid #2a3540',
                borderRadius: 6,
                color: '#eef2f6',
                fontSize: 12,
              }}
              labelFormatter={(l: any) => new Date(l).toLocaleString()}
              formatter={(v: any, name: any) => {
                if (name === 'vwap') return [`${(v as number).toFixed(2)} TZS`, 'VWAP'];
                if (name === 'ewmp') return [`${(v as number).toFixed(2)} TZS`, 'EWM Microprice'];
                return [v, name];
              }}
            />
            <Area
              type="monotone"
              dataKey="vwap"
              stroke="#6366f1"
              strokeWidth={2}
              fill="url(#vwapGradient)"
              fillOpacity={1}
              dot={false}
              isAnimationActive={false}
              connectNulls
            />
            {isLive && ewmpHistory.length > 0 && (
              <Line
                type="monotone"
                dataKey="ewmp"
                stroke="#ff8c3a"
                strokeWidth={1.5}
                dot={false}
                isAnimationActive={false}
                connectNulls
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    );
  };

  return (
    <div style={{
      backgroundColor: '#10151b',
      borderRadius: 8,
      padding: 16,
      border: '1px solid #2a3540',
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 12,
      }}>
        <h3 style={{ margin: 0, color: '#eef2f6', fontSize: 15, fontWeight: 600 }}>
          VWAP — {labelForSecId(actualSymbol)} ({actualSymbol})
        </h3>
        <TimeRangePicker value={range} onChange={setRange} />
      </div>

      {inner()}

      <div style={{
        marginTop: 8,
        fontSize: 11,
        color: '#7a8693',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        <span>
          {data.length} pts
          {lastPoint && ` · ${new Date(lastPoint.timeValue).toLocaleTimeString()}`}
          {!isLive && <span style={{ color: '#6366f1', marginLeft: 6 }}>+ live</span>}
        </span>
        {isLive && microprice != null && microprice > 0 && lastPoint?.vwap != null && (() => {
          const delta = microprice - lastPoint.vwap;
          const pct = lastPoint.vwap > 0 ? (delta / lastPoint.vwap) * 100 : 0;
          return (
            <span style={{ color: '#ff8c3a' }}>
              MP {microprice.toFixed(2)} ({delta >= 0 ? '+' : ''}{pct.toFixed(2)}% vs VWAP)
            </span>
          );
        })()}
      </div>
    </div>
  );
}

function centreBox(height: number): React.CSSProperties {
  return {
    height,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#10151b',
    borderRadius: 8,
    border: '1px solid #2a3540',
  };
}
